import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'
import type { StyleObject } from 'styletron-standard'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface MultiSelectOptionProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  /** Unique value for this option */
  value: string
  /** Whether this option is currently selected */
  selected?: boolean
  /** Whether this option is disabled */
  disabled?: boolean
  /** Whether only some of what this option stands for is selected */
  indeterminate?: boolean
  /** Style merged over the option's own, in the same rule so it always wins */
  overrides?: StyleObject
  /** Drawn before the checkbox, like a tree's guide line */
  leading?: React.ReactNode
  /** Called when this option is clicked */
  onSelect?: (value: string) => void
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled MultiSelectOption component.
 *
 * Represents a single option with checkbox within a MultiSelect dropdown.
 * All visual state determined by props.
 */
const MultiSelectOption = React.memo(
  ({
    value,
    selected = false,
    disabled = false,
    indeterminate = false,
    overrides,
    leading,
    onSelect,
    children,
    style,
    onClick,
    onKeyDown,
    className,
    ...props
  }: MultiSelectOptionProps) => {
    const [css, theme] = useStyletron()

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return
      onSelect?.(value)
      onClick?.(e)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return
      // Enter or Space on a control inside the option belongs to that control
      const isOnOption = e.target === e.currentTarget
      if (isOnOption && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        onSelect?.(value)
      }
      onKeyDown?.(e)
    }

    const optionClassName = css({
      ...getOptionStyles(theme, { selected, disabled }),
      ...overrides,
    })
    const checkboxClassName = css(
      getCheckboxStyles(theme, { selected, disabled })
    )
    const labelClassName = css(getLabelStyles())

    return (
      <div
        role="option"
        aria-selected={selected}
        aria-checked={indeterminate && !selected ? 'mixed' : undefined}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        data-value={value}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`${optionClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {leading}
        <input
          type="checkbox"
          checked={selected}
          ref={el => {
            if (el) el.indeterminate = indeterminate && !selected
          }}
          disabled={disabled}
          onChange={() => {}} // Handled by parent div click
          className={checkboxClassName}
          tabIndex={-1}
          aria-hidden="true"
        />
        <span className={labelClassName}>{children}</span>
      </div>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

interface StyleParams {
  selected: boolean
  disabled: boolean
}

const getOptionStyles = (theme: any, { selected, disabled }: StyleParams) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 12px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '14px',
  lineHeight: '20px',
  color: disabled ? theme.colors.grey50 : theme.colors.grey70,
  backgroundColor: selected ? theme.colors.primary50 : 'transparent',
  transition: 'background-color 150ms ease',
  ':hover': {
    backgroundColor: disabled
      ? 'transparent'
      : selected
      ? theme.colors.primary50
      : theme.colors.grey10,
  },
})

const getCheckboxStyles = (theme: any, { disabled }: StyleParams) => ({
  width: '16px',
  height: '16px',
  flexShrink: 0,
  cursor: disabled ? 'not-allowed' : 'pointer',
  accentColor: theme.colors.primary,
})

const getLabelStyles = () => ({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
})

export default MultiSelectOption
